import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, CardContent, FormControl, FormLabel, Input, Stack, Typography } from '@mui/joy';
import { MdTag } from 'react-icons/md';
import type { Project } from '../../types/project';
import { WorkspaceCardHeader } from '../../components/WorkspaceCardHeader';
import { normalizeLifecycleRef, updateProjectLifecycleRef } from './workspaceLifecycleRef';

interface WorkspaceLifecycleRefCardProps {
  project: Project;
  onProjectPatch: (patch: Partial<Project>) => Promise<void>;
}

const WorkspaceLifecycleRefCard: React.FC<WorkspaceLifecycleRefCardProps> = ({ project, onProjectPatch }) => {
  const current = normalizeLifecycleRef(project.lifecycle_ref);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(current);
  }, [editing, current]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const next = await updateProjectLifecycleRef(project.id, current, draft);
      await onProjectPatch({ lifecycle_ref: next });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update lifecycle ref');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <WorkspaceCardHeader
          icon={MdTag}
          title="Lifecycle reference"
          description="Assigned when this workspace is created. Quotes, estimates, and invoices inherit it."
          action={!editing ? (
            <Button size="sm" variant="plain" onClick={() => setEditing(true)}>
              Override
            </Button>
          ) : undefined}
        />
        {error && <Alert color="danger" sx={{ mb: 1 }}>{error}</Alert>}
        {editing ? (
          <Stack gap={1}>
            <FormControl>
              <FormLabel>Reference</FormLabel>
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value.toUpperCase())}
                placeholder="KSG-0500"
              />
            </FormControl>
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button size="sm" variant="plain" disabled={saving} onClick={() => { setEditing(false); setDraft(current); setError(null); }}>
                Cancel
              </Button>
              <Button size="sm" loading={saving} disabled={!normalizeLifecycleRef(draft)} onClick={() => { void save(); }}>
                Save
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Typography level="title-sm">{current || 'Not assigned yet'}</Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkspaceLifecycleRefCard;
